import { useMemo } from 'react'
import { useMe } from '@/features/auth/useMe'
import {
  useGetMyUnpublishedCoursesQuery,
  useGetMyPublishedCoursesQuery,
} from '@/features/editor/coursesApi'
import { useLanguage } from '@/hooks/useLanguage'
import { courseDisplayName } from '@/lib/localizedText'

export interface RecentCourse {
  id: number
  title: string
}

export interface SidebarData {
  userName: string
  userEmail: string
  userAvatar: string | null
  initials: string
  /** `null` — список ещё грузится (не путать с «курсов нет»). */
  recentCourses: RecentCourse[] | null
}

export const useSidebarData = (): SidebarData => {
  const { data: me } = useMe()
  const { data: drafts } = useGetMyUnpublishedCoursesQuery()
  const { data: published } = useGetMyPublishedCoursesQuery()
  const { language } = useLanguage()

  const myCourses = useMemo(
    () => (drafts || published ? [...(drafts ?? []), ...(published ?? [])] : undefined),
    [drafts, published],
  )

  const userName = me?.username || me?.email?.split('@')[0] || 'User'
  const initials = userName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

  const recentCourses = useMemo(() => {
    if (!myCourses) return null
    return [...myCourses]
      .sort((a, b) => {
        const aDate = a.updatedAt ? new Date(a.updatedAt).getTime() : 0
        const bDate = b.updatedAt ? new Date(b.updatedAt).getTime() : 0
        return bDate - aDate
      })
      .slice(0, 5)
      .map((c) => ({
        id: c.id,
        title: courseDisplayName(c, language),
      }))
  }, [myCourses, language])

  return {
    userName,
    userEmail: me?.email ?? '',
    userAvatar: me?.avatar ?? null,
    initials,
    recentCourses,
  }
}
